// Eklenti Sağlığı — SAF karar katmanı (I/O yok, import yok; test/ext-health-ui.test.mjs esbuild ile
// yükler). Canlı kart çipinin rengi, çekmecenin Durum satırları, Hızlı düzelt'in sıradaki önerisi,
// komut sonuçlarının okunur özeti, günlük satırı ayrıştırma ve hata gruplama burada.
//
// Metin: sayı/ad taşıyan cümleler burada `L(lang, tr, en)` ile İKİ dilde üretilir — DOM sözlüğü
// (i18n.ts) yalnız sabit etiketleri çevirir, sayılı cümle için onlarca regex yerine bu (CLAUDE.md §30).
//
// Eşikler 2026-10-03..09 canlı telemetrisinden: kayıp sonuç günde 0 temsilci (kırmızı nadir kalır);
// panel "donması" saatlere uzar (dizüstü kapağı) → çipe GİRMEZ; giriş sayfasına atılma bugün 10
// temsilcide ≥1 → çip eşiği 3.

export type Lang = 'tr' | 'en';
export type Level = 'ok' | 'warn' | 'bad' | 'none';

export const L = (lang: Lang, tr: string, en: string): string => (lang === 'en' ? en : tr);

/** Giriş sayfasına atılma: bu sayıdan itibaren çipe/listeye girer. */
export const AUTH_LANDING_WARN = 3;
/** Panel donması: Durum satırında amber eşiği. */
export const FREEZE_WARN_MS = 60_000;
/** |saat farkı| bunu aşarsa amber (§13'ün ölçülen p99.9'u 37 sn). */
export const CLOCK_WARN_MS = 120_000;
/** Panel en son yoklamasını bundan eski yaptıysa (sağlıklı panel 30–60 sn'de bir) amber. */
export const POLL_WARN_MS = 180_000;
/** Son 1 saatte bu kadar eklenti HATA satırı → kırmızı çip (1–2 → amber). Faz 2, 2.3.0+. */
export const ERRORS_1H_BAD = 3;

/** Faz 2 komutları (update_extension, factory_reset, debug_logging, snapshot), hata akışı ve "açılışta uygula"
 *  yalnız bu sürümden itibaren (docs/operations/EXT_TOOLKIT_CONTRACT.md). */
export const TOOLKIT_MIN_VERSION = '2.3.0';
export const TOOLKIT_KINDS = new Set(['update_extension', 'factory_reset', 'debug_logging', 'snapshot']);

/** Sayısal parça karşılaştırması (ext-version.ts'in eşi — bu modül import ETMEZ, test tek dosyayı yükler). */
export function atLeast(version: string | undefined, min: string): boolean {
  if (!version) return false;
  const a = version.split('.');
  const b = min.split('.');
  for (let i = 0; i < Math.max(a.length, b.length); i++) {
    const x = Number.parseInt(a[i] ?? '0', 10) || 0;
    const y = Number.parseInt(b[i] ?? '0', 10) || 0;
    if (x !== y) return x > y;
  }
  return true;
}

/** Her komutu ÇALIŞTIRAN ilk sürüm (daha eskisi 'unsupported' der). Listede yoksa kanalın başından (1.0.14) beri.
 *  BetBooker 1.0.17/1.0.20'de kaldı: onarım merdiveni ve Hızlı düzelt bunları çalışır gösterip "Desteklenmiyor" alıyordu. */
const KIND_MIN_VERSION: Record<string, string> = {
  clear_sms_cache: '1.0.21',
  refresh_stats: '1.0.22', clear_rejected_rows: '1.0.22', recover_panel: '1.0.22', tail_logs: '1.0.22',
  reload: '1.0.22', recapture_session: '1.0.22', reset_agent: '1.0.22', export_wal: '1.0.22', drop_wal: '1.0.22',
};
/** Komutun gerektirdiği en düşük sürüm; '' = her sürüm. */
export const minVersionFor = (kind: string): string =>
  TOOLKIT_KINDS.has(kind) ? TOOLKIT_MIN_VERSION : (KIND_MIN_VERSION[kind] || '');

/** Bu sürüm bu komutu çalıştırabilir mi. Sürümü bilinmeyen panelde yalnız araç kiti komutları kapalı (önceki kural). */
export const supportsKind = (version: string | undefined, kind: string): boolean => {
  const min = minVersionFor(kind);
  if (!min) return true;
  if (!version) return !TOOLKIT_KINDS.has(kind);
  return atLeast(version, min);
};

export interface TelemetryLike {
  deadLetters: number;
  writeErrors: number;
  walReplays: number;
  panelSleepMaxMs: number;
  panelSleepCause: string;
  callEventDrops?: number;
  authLandings?: number;
}

export interface AgentLike {
  state: 'working' | 'break' | 'offline';
  panelDark: boolean;
  lastBeatAt: string | null;
  extVersion?: string;
  telemetry: TelemetryLike;
  extErrors1h?: number;
}

export interface Reason { level: Level; text: string }

const worst = (levels: Level[]): Level =>
  levels.includes('bad') ? 'bad' : levels.includes('warn') ? 'warn' : 'ok';

/** Süre: 41 sn · 6 dk · 2 sa 5 dk. */
export function fmtDur(ms: number, lang: Lang): string {
  const s = Math.max(0, Math.round(ms / 1000));
  if (s < 60) return s + L(lang, ' sn', ' s');
  const m = Math.round(s / 60);
  if (m < 60) return m + L(lang, ' dk', ' min');
  const h = Math.floor(m / 60);
  const r = m % 60;
  return h + L(lang, ' sa', ' h') + (r ? ' ' + r + L(lang, ' dk', ' min') : '');
}

/** A — Canlı kart sürüm çipinin sağlık durumu + nedenleri (çip ipucu). */
export function chipHealth(a: AgentLike, outdated: boolean, newest: string, lang: Lang): { level: Level; reasons: Reason[] } {
  const t = a.telemetry;
  const r: Reason[] = [];
  const e1h = a.extErrors1h || 0;
  if (e1h > 0) r.push({ level: e1h >= ERRORS_1H_BAD ? 'bad' : 'warn', text: L(lang, 'Son 1 saatte ' + e1h + ' eklenti hatası', e1h + ' extension errors in the last hour') });
  if (t.deadLetters > 0) r.push({ level: 'bad', text: L(lang, 'Bugün ' + t.deadLetters + ' sonuç kayboldu', t.deadLetters + ' results lost today') });
  if (t.writeErrors > 0) r.push({ level: 'warn', text: L(lang, 'Bugün ' + t.writeErrors + ' yazma hatası', t.writeErrors + ' write errors today') });
  const drops = t.callEventDrops || 0;
  if (drops > 0) r.push({ level: 'warn', text: L(lang, drops + ' çağrı olayı gönderilemedi', drops + ' call events dropped') });
  const al = t.authLandings || 0;
  if (al >= AUTH_LANDING_WARN) r.push({ level: 'warn', text: L(lang, 'Bugün ' + al + ' kez giriş sayfasına atıldı', 'Sent to the login page ' + al + ' times today') });
  if (outdated) r.push({ level: 'warn', text: L(lang, 'Sürüm ' + a.extVersion + ' · en yeni ' + newest, 'Version ' + a.extVersion + ' · newest is ' + newest) });
  return { level: r.length ? worst(r.map((x) => x.level)) : 'ok', reasons: r };
}

// ── Komutlar ────────────────────────────────────────────────────────────────

export interface LadderStep { n: number; kind: string; tier: 1 | 2 | 3; isNew?: boolean }

/** Onarım merdiveni (tasarım B · İşlemler): güvenliden yıkıcıya. Etiket/açıklama JSX'te (sözlük). 8–9 = 2.3.0+. */
export const LADDER: LadderStep[] = [
  { n: 1, kind: 'ping', tier: 1 },
  { n: 2, kind: 'diagnostics', tier: 1 },
  { n: 3, kind: 'flush_wal', tier: 1 },
  { n: 4, kind: 'clear_cache', tier: 1 },
  { n: 5, kind: 'recover_panel', tier: 2 },
  { n: 6, kind: 'reload', tier: 2 },
  { n: 7, kind: 'reset_agent', tier: 3 },
  { n: 8, kind: 'update_extension', tier: 2, isNew: true },
  { n: 9, kind: 'factory_reset', tier: 3, isNew: true },
];

/** Komut adının iki dilli görünen adı (geçmiş satırı, şerit, zaman çizelgesi). */
export function kindLabel(kind: string, lang: Lang): string {
  const M: Record<string, [string, string]> = {
    ping: ['Bağlantı testi', 'Ping'],
    diagnostics: ['Durum raporu', 'Diagnostics'],
    show_message: ['Mesaj', 'Message'],
    set_sheet: ['Sayfa değiştir', 'Change sheet'],
    reset_queue_cursor: ['Sıra imlecini sıfırla', 'Reset queue cursor'],
    clear_rejected_rows: ['Atlanan satırları temizle', 'Clear skipped rows'],
    reset_break: ['Mola durumunu sıfırla', 'Reset break state'],
    flush_wal: ['Bekleyen sonuçları gönder', 'Send pending results'],
    clear_cache: ['Panel önbelleğini temizle', 'Clear panel cache'],
    clear_sms_cache: ['SMS şablonlarını yenile', 'Refresh SMS templates'],
    refresh_stats: ['İstatistikleri yenile', 'Refresh stats'],
    recover_panel: ['Yumuşak onarım', 'Soft repair'],
    tail_logs: ['Günlükleri al', 'Fetch logs'],
    reload: ['Sayfayı yenile', 'Reload page'],
    recapture_session: ['Oturumu yeniden al', 'Recapture session'],
    reset_agent: ['Paneli tamamen sıfırla', 'Full panel reset'],
    export_wal: ['Bekleyenleri listele', 'List pending results'],
    drop_wal: ['Bekleyenleri düşür', 'Drop pending results'],
    update_extension: ['Eklentiyi güncelle', 'Update extension'],
    factory_reset: ['Fabrika ayarlarına döndür', 'Factory reset'],
    debug_logging: ['Hata ayıklama modu', 'Debug mode'],
    snapshot: ['Anlık görüntü', 'Snapshot'],
  };
  const m = M[kind];
  return m ? L(lang, m[0], m[1]) : kind;
}

/** Çekmecenin onay kuralı (tasarım): 1 tek tık · 2 onay penceresi · 3 adını yaz. Sunucu tier'ından
 *  (src/api/commands.js COMMAND_TIERS) iki fark: reset_agent 3 (isim sorusunu yeniden açar),
 *  tail_logs 1 (salt okunur — Günlük sekmesinin "yenile"si onaysız olmalı). */
export function confirmTier(kind: string): 1 | 2 | 3 {
  if (kind === 'reset_agent' || kind === 'export_wal' || kind === 'drop_wal' || kind === 'factory_reset') return 3;
  if (kind === 'reload' || kind === 'recapture_session' || kind === 'recover_panel' || kind === 'update_extension') return 2;
  return 1;
}

export type CmdStatus = 'pending' | 'delivered' | 'accepted' | 'done' | 'failed' | 'refused' | 'unsupported' | 'expired';

export const isLiveStatus = (s: string): boolean => s === 'pending' || s === 'delivered' || s === 'accepted';

const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);

/** diagnostics.rejectedRow → {sheet, row} or null. The extension sends the OBJECT (orchestrator `rejectedRow:
 *  {sheet, row}`); reading it as a number hid every poison row — Health said ok, Quick fix never offered
 *  clear_rejected_rows, the report showed "[object Object]". A bare number is accepted too. */
export function rejectedRowOf(v: unknown): { sheet: string; row: number } | null {
  if (typeof v === 'number' && Number.isFinite(v)) return { sheet: '', row: v };
  const o = v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
  return o && typeof o.row === 'number' && Number.isFinite(o.row)
    ? { sheet: typeof o.sheet === 'string' ? o.sheet : '', row: o.row } : null;
}
/** "KAYIT · 412" (or "412" with no sheet) — the one way the panel prints it. */
export const fmtRejectedRow = (r: { sheet: string; row: number }): string => (r.sheet ? r.sheet + ' · ' : '') + r.row;
const str = (v: unknown): string => (typeof v === 'string' ? v : '');

/** Komut sonucu → tek satırlık okunur özet (JSON yerine). */
export function summarizeResult(kind: string, status: string, result: Record<string, unknown> | null, lang: Lang, tz = 'Europe/Istanbul'): string {
  const r = result || {};
  const reason = str(r.reason);
  if (status === 'pending') return L(lang, 'Gönderildi · panelin yoklaması bekleniyor', 'Sent · waiting for the panel’s poll');
  if (status === 'delivered') return L(lang, 'Panele ulaştı', 'Delivered to the panel');
  if (status === 'accepted') return L(lang, 'Çalışıyor', 'Running');
  if (status === 'expired') return L(lang, 'Süresi doldu · panel açık değildi', 'Expired · the panel was not open');
  if (status === 'unsupported') return L(lang, 'Bu eklenti sürümü desteklemiyor', 'Not supported by this extension version');
  if (status === 'refused') {
    if (reason === 'agent_entering_result') return L(lang, 'Temsilci sonuç giriyordu', 'Agent was entering a result');
    if (reason === 'cancelled_by_supervisor') return L(lang, 'Panel almadan iptal edildi', 'Cancelled before the panel ran it');
    if (reason === 'no_active_sheet') return L(lang, 'Aktif sayfa yok', 'No active sheet');
    if (kind === 'flush_wal') {
      if (str(r.skipped) === 'busy') return L(lang, 'Zaten gönderiyordu', 'Already sending');
      if (str(r.skipped) === 'no_creds') return L(lang, 'BackOffice anahtarı yok', 'No BackOffice key');
      const leased = num(r.leased);
      if (leased) return L(lang, leased + ' sonuç başka sekmede gönderiliyor', leased + ' results being sent by another tab');
    }
    return reason ? L(lang, 'Reddedildi: ' + reason, 'Refused: ' + reason) : L(lang, 'Reddedildi', 'Refused');
  }
  if (status === 'failed') {
    const e = str(r.error) || reason;
    return e ? L(lang, 'Başarısız: ' + e, 'Failed: ' + e) : L(lang, 'Başarısız', 'Failed');
  }
  // done
  switch (kind) {
    case 'ping': {
      const v = str(r.version);
      return v ? L(lang, 'Ulaşıldı · v' + v, 'Reachable · v' + v) : L(lang, 'Ulaşıldı', 'Reachable');
    }
    case 'diagnostics': return L(lang, Object.keys(r).length + ' alan okundu', Object.keys(r).length + ' fields read');
    case 'tail_logs': {
      const n = Array.isArray(r.lines) ? r.lines.length : 0;
      return L(lang, n + ' günlük satırı', n + ' log lines');
    }
    case 'flush_wal': {
      const wrote = num(r.wrote) ?? 0;
      const rem = num(r.remaining);
      const tail = rem != null && rem >= 0 ? L(lang, ' · ' + rem + ' kaldı', ' · ' + rem + ' left') : '';
      return L(lang, wrote + ' sonuç gönderildi', wrote + ' results sent') + tail;
    }
    case 'clear_sms_cache': {
      const t = num(r.templates);
      return t != null ? L(lang, t + ' şablon yüklendi', t + ' templates loaded') : L(lang, 'Şablonlar yenilendi', 'Templates refreshed');
    }
    case 'refresh_stats': {
      const d = num(r.todayDone);
      return d != null ? L(lang, 'Bugün ' + d + ' müşteri', d + ' customers today') : L(lang, 'Sayaçlar yenilendi', 'Counters refreshed');
    }
    case 'set_sheet':
    case 'reset_queue_cursor':
    case 'clear_rejected_rows': {
      const s = str(r.sheet);
      return s ? L(lang, 'Sayfa ' + s, 'Sheet ' + s) : L(lang, 'Tamamlandı', 'Done');
    }
    case 'export_wal': {
      const t = num(r.total) ?? 0;
      return L(lang, t + ' bekleyen sonuç', t + ' pending results');
    }
    case 'clear_cache': return L(lang, 'Önbellek temizlendi', 'Cache cleared');
    case 'recover_panel': return L(lang, 'Panel yerinde yeniden başladı', 'Panel restarted in place');
    case 'reload': return L(lang, 'Sayfa yenilendi', 'Page reloaded');
    case 'reset_agent': return L(lang, 'Panel sıfırlandı · ad yeniden soruldu', 'Panel reset · name asked again');
    case 'recapture_session': return L(lang, 'Oturum yeniden yakalanıyor', 'Session being recaptured');
    case 'reset_break': return L(lang, 'Mola durumu sıfırlandı', 'Break state reset');
    case 'show_message': return L(lang, 'Mesaj gösterildi', 'Message shown');
    case 'update_extension': {
      const check = str(r.check);
      const cur = str(r.current);
      const av = str(r.available);
      // The done ack leaves BEFORE the apply, which waits for an idle panel (≤ 4 min) and may stand down — never claim
      // the reload happened.
      if (check === 'update_available') return L(lang, 'Güncelleme bulundu' + (av ? ' (' + av + ')' : '') + ' · panel boşta olunca yüklenir', 'Update found' + (av ? ' (' + av + ')' : '') + ' · applies when the panel is idle');
      if (check === 'no_update') return L(lang, 'Zaten güncel' + (cur ? ' (' + cur + ')' : ''), 'Already up to date' + (cur ? ' (' + cur + ')' : ''));
      if (check === 'throttled') return L(lang, 'Chrome sınırladı · biraz sonra tekrar deneyin', 'Chrome throttled the check · try again later');
      return L(lang, 'Kontrol edildi', 'Checked');
    }
    case 'factory_reset': {
      const n = num(r.cleared);
      return L(lang, (n != null ? n + ' ayar silindi · ' : '') + 'panel yeniden kuruluyor', (n != null ? n + ' settings cleared · ' : '') + 'panel re-onboarding');
    }
    case 'debug_logging': {
      const until = str(r.until);
      if (!until) return L(lang, 'Hata ayıklama kapatıldı', 'Debug mode off');
      const d = new Date(until);
      const hm = Number.isNaN(d.getTime()) ? '' : d.toLocaleTimeString(lang === 'en' ? 'en-GB' : 'tr-TR', { hour: '2-digit', minute: '2-digit', timeZone: tz });
      return L(lang, 'Hata ayıklama açık' + (hm ? ' · kapanış ' + hm : ''), 'Debug mode on' + (hm ? ' · until ' + hm : ''));
    }
    case 'snapshot':
      return r.reported === true
        ? L(lang, 'Anlık görüntü alındı · rapor kutusunda', 'Snapshot taken · in the reports inbox')
        : L(lang, 'Anlık görüntü gönderilemedi', 'Snapshot could not be sent') + (str(r.error) ? ' · ' + str(r.error) : '');
    default: return L(lang, 'Tamamlandı', 'Done');
  }
}

/** Eklenti sayfası "Eski sürümdekiler" satırı: bir update_extension'ın gönderimden yüklemeye kadar izi. `status` =
 *  komutun durumu, ya da 'sending' (henüz POST'ta) / 'send_failed' (`sendError` = sunucu kodu). `applyTimedOut` =
 *  update_available geldi ama temsilcinin sürümü yükleme penceresinde değişmedi. Sürüm değişince satır listeden
 *  kendisi düşer; bu fonksiyon o anı çizmez. */
export interface UpdateTrack {
  status: string;
  result?: Record<string, unknown> | null;
  sendError?: string;
  applyTimedOut?: boolean;
}
export interface UpdateView {
  text: string;
  tone: 'info' | 'warn' | 'bad';
  /** Güncelle düğmesi yeniden gösterilir (bir sonuç geldi, yeniden denemek anlamlı). */
  retry: boolean;
  /** update_available geldi: liste sık yenilenir, satır yeni sürümle düşer. */
  applying: boolean;
}
export function updateView(t: UpdateTrack, lang: Lang): UpdateView {
  const r = t.result || {};
  const v = (text: string, tone: UpdateView['tone'], retry = true, applying = false): UpdateView => ({ text, tone, retry, applying });
  switch (t.status) {
    case 'sending': return v(L(lang, 'Gönderiliyor…', 'Sending…'), 'info', false);
    case 'send_failed':
      return t.sendError === 'command_in_flight'
        ? v(L(lang, 'Başka bir komut bekliyor', 'Another command is pending'), 'warn')
        : v(L(lang, 'Gönderilemedi', 'Not sent') + (t.sendError ? ' · ' + t.sendError : ''), 'bad');
    case 'pending': return v(L(lang, 'Gönderildi · panelin yoklaması bekleniyor', 'Sent · waiting for the panel’s poll'), 'info', false);
    case 'delivered':
    case 'accepted': return v(L(lang, 'Güncelleme aranıyor…', 'Checking for the update…'), 'info', false);
    case 'expired': return v(L(lang, 'Panel komutu almadı · süresi doldu', 'The panel never picked it up · expired'), 'warn');
    case 'unsupported': return v(L(lang, 'Bu sürüm uzaktan güncellenemez', 'This version cannot update remotely'), 'bad', false);
    case 'refused':
      return str(r.reason) === 'agent_entering_result'
        ? v(L(lang, 'Temsilci sonuç giriyordu · tekrar deneyin', 'Agent was entering a result · try again'), 'warn')
        : v(summarizeResult('update_extension', 'refused', t.result ?? null, lang), 'warn');
    case 'failed': return v(summarizeResult('update_extension', 'failed', t.result ?? null, lang), 'bad');
    case 'done': {
      const check = str(r.check);
      const av = str(r.available);
      if (check === 'update_available') {
        // The ack leaves BEFORE the apply, which waits (≤ 4 min) for an idle panel and may stand down.
        return t.applyTimedOut
          ? v(L(lang, 'Henüz yüklenmedi · panel boşalınca kendisi yükler', 'Not installed yet · installs itself once the panel is idle'), 'warn')
          : v(L(lang, (av ? 'v' + av + ' yükleniyor' : 'Yükleniyor') + ' · panel boşta olunca', 'Updating' + (av ? ' to v' + av : '') + ' · when the panel is idle'), 'info', false, true);
      }
      // An outdated agent answered "no update": Chrome's update server has not offered this browser the new build yet.
      if (check === 'no_update') return v(L(lang, 'Mağaza bu tarayıcıya henüz yeni sürümü vermedi', 'The store has not offered this browser the update yet'), 'warn');
      if (check === 'throttled') return v(L(lang, 'Chrome sınırladı · birkaç dakika sonra tekrar deneyin', 'Chrome throttled the check · try again in a few minutes'), 'warn');
      return v(L(lang, 'Kontrol edildi', 'Checked'), 'warn');
    }
    default: return v(t.status, 'warn');
  }
}

/** Komut durumunun kısa etiketi + tonu (geçmiş rozetleri). */
export function statusBadge(status: string, lang: Lang): { text: string; level: Level | 'info' } {
  switch (status) {
    case 'done': return { text: L(lang, 'Tamam', 'Done'), level: 'ok' };
    case 'refused': return { text: L(lang, 'Reddedildi', 'Refused'), level: 'warn' };
    case 'unsupported': return { text: L(lang, 'Desteklenmiyor', 'Unsupported'), level: 'warn' };
    case 'failed': return { text: L(lang, 'Başarısız', 'Failed'), level: 'bad' };
    case 'expired': return { text: L(lang, 'Süresi doldu', 'Expired'), level: 'none' };
    case 'pending': return { text: L(lang, 'Gönderildi', 'Sent'), level: 'info' };
    case 'delivered': return { text: L(lang, 'Ulaştı', 'Delivered'), level: 'info' };
    case 'accepted': return { text: L(lang, 'Çalışıyor', 'Running'), level: 'info' };
    default: return { text: status, level: 'none' };
  }
}

// ── B · Durum satırları ─────────────────────────────────────────────────────

export interface HealthRow {
  key: string;
  label: string;
  level: Level;
  reason: string;
  /** Bu satırı düzelten komut (yoksa yok). */
  fix?: string;
  note?: string;
}

const obj = (v: unknown): Record<string, unknown> | null =>
  v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null;

/**
 * Durum listesi. `diag` = son `diagnostics` sonucu (yoksa null: o satırlar "Ölçülmedi"), `clockMs`
 * = sunucu − temsilci saati (call_sessions, 2.1.0+). Sıra = tasarımın sırası.
 */
export function healthRows(
  a: AgentLike,
  diag: Record<string, unknown> | null,
  clockMs: number | null,
  outdated: boolean,
  newest: string,
  now: number,
  lang: Lang,
): HealthRow[] {
  const rows: HealthRow[] = [];
  const noDiag = L(lang, 'Durum raporu yok — "Durum raporu al" çalıştırın', 'No diagnostics yet — run Diagnostics');
  const t = a.telemetry;

  // 1. Proxy bağlantısı — heartbeat.
  const beatMs = a.lastBeatAt ? now - new Date(a.lastBeatAt).getTime() : null;
  if (a.panelDark) {
    rows.push({ key: 'conn', label: L(lang, 'Proxy bağlantısı', 'Connection to proxy'), level: 'bad',
      reason: beatMs != null
        ? L(lang, 'Heartbeat kesildi · son ' + fmtDur(beatMs, lang) + ' önce', 'No heartbeat for ' + fmtDur(beatMs, lang))
        : L(lang, 'Heartbeat yok', 'No heartbeat') });
  } else if (a.state === 'offline' || beatMs == null) {
    rows.push({ key: 'conn', label: L(lang, 'Proxy bağlantısı', 'Connection to proxy'), level: 'none',
      reason: L(lang, 'Panel kapalı', 'Panel closed') + (beatMs != null ? L(lang, ' · son heartbeat ' + fmtDur(beatMs, lang) + ' önce', ' · last heartbeat ' + fmtDur(beatMs, lang) + ' ago') : '') });
  } else {
    rows.push({ key: 'conn', label: L(lang, 'Proxy bağlantısı', 'Connection to proxy'), level: 'ok',
      reason: L(lang, 'Son heartbeat ' + fmtDur(beatMs, lang) + ' önce', 'Last heartbeat ' + fmtDur(beatMs, lang) + ' ago') });
  }

  // 2. Eklenti bağlamı (2.x: güncelleme açık sekmenin altında olduysa `stale`).
  if (diag && diag.stale === true) {
    rows.push({ key: 'stale', label: L(lang, 'Eklenti bağlamı', 'Extension context'), level: 'bad',
      reason: L(lang, 'Eklenti sekmenin altında güncellendi · sayfa yenilenmeli', 'Extension updated under the open tab · page needs a reload'), fix: 'reload' });
  }

  // 3. BackOffice oturumu.
  {
    const label = L(lang, 'BackOffice oturumu', 'BackOffice session');
    if (!diag) rows.push({ key: 'session', label, level: 'none', reason: noDiag });
    else if (diag.sessionLost === true) rows.push({ key: 'session', label, level: 'bad', reason: L(lang, 'Oturum düştü', 'Session lost'), fix: 'recapture_session' });
    else if (diag.hasCredentials === false) rows.push({ key: 'session', label, level: 'bad', reason: L(lang, 'BackOffice anahtarı yakalanmadı', 'BackOffice key not captured'), fix: 'recapture_session' });
    else if (diag.awaitingCreds === true) rows.push({ key: 'session', label, level: 'warn', reason: L(lang, 'Anahtar bekleniyor', 'Waiting for the key') });
    else rows.push({ key: 'session', label, level: 'ok', reason: L(lang, 'Oturum açık', 'Signed in') + (str(diag.sessionState) ? ' · ' + str(diag.sessionState) : '') });
  }

  // 4. Softphone.
  {
    const label = L(lang, 'Softphone', 'Softphone');
    const ph = obj(diag?.phone);
    const st = str(ph?.status);
    if (!diag) rows.push({ key: 'phone', label, level: 'none', reason: noDiag });
    else if (!ph || !st) rows.push({ key: 'phone', label, level: 'none', reason: L(lang, 'Bu sürüm bildirmiyor', 'Not reported by this version') });
    else if (st === 'ready') rows.push({ key: 'phone', label, level: 'ok', reason: L(lang, 'Kayıtlı · hazır', 'Registered · ready') });
    else if (st === 'calling' || st === 'in_call' || st === 'ending') rows.push({ key: 'phone', label, level: 'ok', reason: L(lang, 'Görüşmede', 'On a call') });
    else if (st === 'registering') rows.push({ key: 'phone', label, level: 'warn', reason: L(lang, 'Kaydoluyor', 'Registering') });
    else if (st === 'disabled') rows.push({ key: 'phone', label, level: 'none', reason: L(lang, 'Bu marka/temsilci için kapalı', 'Off for this brand/agent') });
    else if (st === 'no_mic') rows.push({ key: 'phone', label, level: 'bad', reason: L(lang, 'Mikrofon izni verilmemiş · temsilci izin vermeli', 'Microphone not allowed · the agent must grant it') });
    else if (st === 'offline') rows.push({ key: 'phone', label, level: 'warn', reason: L(lang, 'Santrale bağlı değil', 'Not connected to the PBX'), fix: 'recover_panel' });
    else rows.push({ key: 'phone', label, level: 'bad', reason: L(lang, 'Hata', 'Error') + (str(ph.error) ? ': ' + str(ph.error) : ''), fix: 'recover_panel' });
  }

  // 5. Çalışma sayfası + yoklama.
  {
    const label = L(lang, 'Çalışma sayfası', 'Sheet access');
    if (!diag) rows.push({ key: 'sheet', label, level: 'none', reason: noDiag });
    else {
      const sheet = str(diag.sheet);
      const rej = rejectedRowOf(diag.rejectedRow);
      const poll = num(diag.lastPollAgeMs);
      if (!sheet) rows.push({ key: 'sheet', label, level: 'warn', reason: L(lang, 'Sayfa seçili değil', 'No sheet selected') });
      else if (rej != null) rows.push({ key: 'sheet', label, level: 'warn', reason: L(lang, (rej.sheet || sheet) + ' · satır ' + rej.row + ' reddedildi', (rej.sheet || sheet) + ' · row ' + rej.row + ' was refused'), fix: 'clear_rejected_rows' });
      else if (poll != null && poll > POLL_WARN_MS) rows.push({ key: 'sheet', label, level: 'warn', reason: L(lang, sheet + ' · son yoklama ' + fmtDur(poll, lang) + ' önce', sheet + ' · last poll ' + fmtDur(poll, lang) + ' ago'), fix: 'recover_panel' });
      else rows.push({ key: 'sheet', label, level: 'ok', reason: sheet });
    }
  }

  // 6. Bekleyen sonuçlar (WAL).
  {
    const label = L(lang, 'Bekleyen sonuçlar', 'Pending results queue');
    const w = num(diag?.walTotal);
    if (!diag || w == null) rows.push({ key: 'wal', label, level: 'none', reason: noDiag });
    else if (w > 0) rows.push({ key: 'wal', label, level: 'bad', reason: L(lang, w + ' sonuç "kuyrukta" bekliyor', w + ' results stuck in "queued"'), fix: 'flush_wal' });
    else rows.push({ key: 'wal', label, level: 'ok', reason: L(lang, 'Boş', 'Empty') });
  }

  // 7. Kayıp sonuç (bugün) — komutla geri gelmez.
  rows.push(t.deadLetters > 0
    ? { key: 'lost', label: L(lang, 'Kayıp sonuç (bugün)', 'Lost results today'), level: 'bad',
      reason: L(lang, t.deadLetters + ' sonuç panele hiç ulaşmadı', t.deadLetters + ' results never reached the panel'),
      note: L(lang, 'Komutla geri alınamaz', 'Can’t be recovered by a command') }
    : { key: 'lost', label: L(lang, 'Kayıp sonuç (bugün)', 'Lost results today'), level: 'ok', reason: L(lang, 'Yok', 'None') });

  // 8. Mola durumu.
  if (diag && (diag.pendingBreakOff === true || (diag.onBreak === true && diag.breakSynced === false))) {
    rows.push({ key: 'break', label: L(lang, 'Mola durumu', 'Break state'), level: 'warn',
      reason: diag.pendingBreakOff === true ? L(lang, 'Mola bitişi gönderilemedi', 'Break end not sent') : L(lang, 'Mola sunucuyla eşleşmedi', 'Break not synced with the server'),
      fix: 'reset_break' });
  }

  // 9. Saat farkı.
  {
    const label = L(lang, 'Saat farkı', 'Clock skew');
    if (clockMs == null) rows.push({ key: 'clock', label, level: 'none', reason: L(lang, 'Ölçülmedi — 2.1.0 ve sonrası bildirir', 'Not measured — 2.1.0+ reports it') });
    else {
      const ahead = clockMs < 0; // offset = sunucu − temsilci; negatif = temsilcinin saati İLERİ
      const d = fmtDur(Math.abs(clockMs), lang);
      rows.push({ key: 'clock', label, level: Math.abs(clockMs) > CLOCK_WARN_MS ? 'warn' : 'ok',
        reason: Math.abs(clockMs) < 1000 ? L(lang, 'Sunucuyla aynı', 'In sync with the server')
          : ahead ? L(lang, d + ' ileri', d + ' ahead') : L(lang, d + ' geri', d + ' behind') });
    }
  }

  // 10. Panel donmaları (bugün).
  rows.push(t.panelSleepMaxMs > 0
    ? { key: 'freeze', label: L(lang, 'Panel donmaları (bugün)', 'Panel freezes today'), level: t.panelSleepMaxMs >= FREEZE_WARN_MS ? 'warn' : 'ok',
      reason: L(lang, 'En uzun ' + fmtDur(t.panelSleepMaxMs, lang), 'Longest ' + fmtDur(t.panelSleepMaxMs, lang)) + (t.panelSleepCause ? ' · ' + t.panelSleepCause : '') }
    : { key: 'freeze', label: L(lang, 'Panel donmaları (bugün)', 'Panel freezes today'), level: 'ok', reason: L(lang, 'Yok', 'None') });

  // 10b. Eklenti hataları (son 1 saat) — 2.3.0'ın gönderdiği satırlar; eski sürümde ölçülmez.
  {
    const label = L(lang, 'Eklenti hataları (son 1 saat)', 'Extension errors (last hour)');
    const e1h = a.extErrors1h || 0;
    if (!atLeast(a.extVersion, TOOLKIT_MIN_VERSION)) rows.push({ key: 'errors', label, level: 'none', reason: L(lang, 'Ölçülmedi — 2.3.0 ve sonrası gönderir', 'Not measured — 2.3.0+ sends them') });
    else rows.push({ key: 'errors', label, level: e1h >= ERRORS_1H_BAD ? 'bad' : e1h > 0 ? 'warn' : 'ok',
      reason: e1h ? L(lang, e1h + ' hata · Hatalar sekmesine bakın', e1h + ' errors · see the Errors tab') : L(lang, 'Yok', 'None') });
  }

  // 11. Uyarılar (bugün): yazma hatası, düşen çağrı olayı, giriş sayfası.
  {
    const parts: string[] = [];
    if (t.writeErrors > 0) parts.push(L(lang, t.writeErrors + ' yazma hatası', t.writeErrors + ' write errors'));
    if ((t.callEventDrops || 0) > 0) parts.push(L(lang, t.callEventDrops + ' çağrı olayı düştü', t.callEventDrops + ' call events dropped'));
    if ((t.authLandings || 0) > 0) parts.push(L(lang, t.authLandings + ' kez giriş sayfası', t.authLandings + ' login-page landings'));
    if (t.walReplays > 0) parts.push(L(lang, t.walReplays + ' yeniden gönderim', t.walReplays + ' replays'));
    const warn = t.writeErrors > 0 || (t.callEventDrops || 0) > 0 || (t.authLandings || 0) >= AUTH_LANDING_WARN;
    rows.push({ key: 'warnings', label: L(lang, 'Uyarılar (bugün)', 'Warnings today'), level: warn ? 'warn' : 'ok', reason: parts.length ? parts.join(' · ') : L(lang, 'Yok', 'None') });
  }

  // 12. Sürüm.
  {
    const v = a.extVersion || '';
    const label = L(lang, 'Sürüm', 'Version');
    if (!v) rows.push({ key: 'version', label, level: 'none', reason: L(lang, 'Henüz görülmedi', 'Not seen yet') });
    else if (outdated && atLeast(v, TOOLKIT_MIN_VERSION)) rows.push({ key: 'version', label, level: 'warn', reason: L(lang, v + ' · en yeni ' + newest, v + ' · newest is ' + newest), fix: 'update_extension' });
    else if (outdated) rows.push({ key: 'version', label, level: 'warn', reason: L(lang, v + ' · en yeni ' + newest, v + ' · newest is ' + newest),
      note: L(lang, 'Chrome birkaç saat içinde kendisi günceller; hemen almak için Chrome yeniden başlatılmalı', 'Chrome updates within hours; restarting Chrome applies it now') });
    else rows.push({ key: 'version', label, level: 'ok', reason: L(lang, v + ' · en yeni', v + ' · newest') });
  }
  return rows;
}

/** Hızlı düzelt: düzeltilebilir ilk kırmızı, yoksa ilk amber satırın komutu. Öncelik = satır sırası. */
export function nextFix(rows: HealthRow[]): HealthRow | null {
  return rows.find((r) => r.fix && r.level === 'bad') || rows.find((r) => r.fix && r.level === 'warn') || null;
}

// ── Günlük ─────────────────────────────────────────────────────────────────

export interface LogLine { t: string; level: 'warn' | 'error'; cat: string; msg: string }

/** İstanbul = UTC+3 sabit (2016'dan beri yaz saati yok). Eklenti damgası UTC `HH:MM:SS`. */
function utcToIst(hms: string): string {
  const m = /^(\d\d):(\d\d):(\d\d)$/.exec(hms);
  if (!m) return hms;
  const h = (Number(m[1]) + 3) % 24;
  return String(h).padStart(2, '0') + ':' + m[2] + ':' + m[3];
}

/** UTC "HH:MM:SS" → the same instant's wall clock in `tz` (the drawer's brand zone; BetBooker = Accra, not +3). */
function utcToTz(hms: string, tz: string): string {
  const m = /^(\d\d):(\d\d):(\d\d)$/.exec(hms);
  if (!m) return hms;
  if (tz === 'Europe/Istanbul') return utcToIst(hms);
  try {
    const d = new Date();
    d.setUTCHours(Number(m[1]), Number(m[2]), Number(m[3]), 0);
    return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false, timeZone: tz });
  } catch { return utcToIst(hms); }
}

/** `tail_logs` satırı: "HH:MM:SS W [cat-sip] mesaj" (logBuffer.getLogTail). Bozuk satır null. 2.3.0'nin debug
 *  modundaki I (bilgi) satırları hata listesine girmez — null, ama "N satır" sayısı da bunları saymamalı. */
export function parseLogLine(line: string, tz = 'Europe/Istanbul'): LogLine | null {
  const m = /^(\d\d:\d\d:\d\d|\?\?:\?\?:\?\?) ([WE]) (.*)$/.exec(line);
  if (!m) return null;
  const msg = m[3];
  const c = /^\[([^\]]{1,24})\]\s*/.exec(msg);
  return { t: utcToTz(m[1], tz), level: m[2] === 'E' ? 'error' : 'warn', cat: c ? c[1] : '', msg: c ? msg.slice(c[0].length) : msg };
}

export interface ErrorGroup { key: string; level: 'warn' | 'error'; cat: string; message: string; count: number; first: string; last: string; lines: LogLine[] }

/** Parmak izi: rakam dizileri ve "(retry n/m)" eki atılır → aynı hata tek grupta. Seviye parmak
 *  izine GİRMEZ: eklenti aynı arızayı önce warn (tekrar denerken) sonra error (vazgeçince) yazar. */
export function fingerprint(l: LogLine): string {
  return (l.cat + '|' + l.msg.replace(/\(retry \d+\/\d+\)/g, '').replace(/#{3,}/g, '#').replace(/\d+/g, '#').replace(/\s+/g, ' ').trim()).slice(0, 160);
}

/** Satırları gruplar: en çok tekrarlanan önce, eşitlikte en yeni önce. Satırlar kronolojik gelir. */
export function groupErrors(lines: LogLine[]): ErrorGroup[] {
  const map = new Map<string, ErrorGroup>();
  for (const l of lines) {
    const k = fingerprint(l);
    const g = map.get(k);
    if (g) { g.count++; g.last = l.t; g.lines.push(l); if (l.level === 'error') g.level = 'error'; }
    else map.set(k, { key: k, level: l.level, cat: l.cat, message: l.msg.replace(/\(retry \d+\/\d+\)/g, '').trim(), count: 1, first: l.t, last: l.t, lines: [l] });
  }
  return [...map.values()].sort((a, b) => b.count - a.count || (a.last < b.last ? 1 : a.last > b.last ? -1 : 0));
}
